import type { Entity, MetadataAttachment } from '@jms/model';

export type BoardAttachmentGroup = {
  key: string;
  title: string;
  items: MetadataAttachment[];
};

type GroupValue = { key: string; title: string };

const noGroup: GroupValue = { key: '__none__', title: 'No Group' };

/**
 * Groups attachment rows for the Attachments board body.
 * Mirrors legacy files-dashboard groupBy: entityName default, array fields (tags, taskItems) explode to one
 * bucket per entry, "No Group" last.
 */
export function groupBoardAttachments(items: readonly MetadataAttachment[], groupBy: string | null | undefined): BoardAttachmentGroup[] {

  const field = groupBy && groupBy !== 'none' ? groupBy : null;
  if (!field || !items.length) {
    return items.length
      ? [{ key: 'all', title: 'All', items: [...items] }]
      : [];
  }

  return sinkNoGroup(groupByField(items, field));
}

function groupByField(items: readonly MetadataAttachment[], field: string): BoardAttachmentGroup[] {

  const buckets = new Map<string, BoardAttachmentGroup>();

  for (const item of items) {
    for (const { key, title } of getGroupValues(item, field)) {
      const bucket = buckets.get(key) ?? { key, title, items: [] };
      bucket.items.push(item);
      buckets.set(key, bucket);
    }
  }

  return [...buckets.values()];
}

/** First-seen order for real groups; "No Group" always last. */
function sinkNoGroup(groups: BoardAttachmentGroup[]): BoardAttachmentGroup[] {

  const trailing = groups.find(group => group.key === noGroup.key);
  if (!trailing) return groups;
  return [...groups.filter(group => group.key !== noGroup.key), trailing];
}

/** Array fields yield one value per entry (a row can sit in several groups); empty arrays fall to "No Group". */
function getGroupValues(item: MetadataAttachment, field: string): GroupValue[] {

  const value = (item as Record<string, unknown>)[field];
  if (!Array.isArray(value)) return [toGroupValue(value)];

  const values = value.filter(entry => entry != null).map(toGroupValue);
  return values.length ? values : [noGroup];
}

function toGroupValue(value: unknown): GroupValue {

  if (value == null || value === '') return noGroup;

  if (typeof value === 'object' && ('_id' in value || 'name' in value)) {
    const named = value as Partial<Entity>;
    const key = named._id ?? named.name;
    return key == null ? noGroup : { key: String(key), title: String(named.name ?? key) };
  }

  const text = String(value);
  return { key: text, title: text };
}
